import { money, type Money } from '@shared/lib';

/**
 * Split a total (MINOR units) equally across `ids`. The floor goes to everyone; the leftover
 * 1-minor-unit remainder is distributed to the LAST few ids (matches the Flutter audit's "remainder
 * spread ±1 minor unit from last user backward"), so the parts always sum back to `total`.
 *
 * e.g. 1000¢ / 3 → { …333, …333, …334 }.
 */
export function equalSplitMinor(totalMinor: number, ids: readonly string[]): Record<string, Money> {
  const out: Record<string, Money> = {};
  const n = ids.length;
  if (n === 0) return out;
  const base = Math.floor(totalMinor / n);
  const remainder = totalMinor - base * n; // 0 .. n-1
  ids.forEach((id, i) => {
    out[id] = money(base + (i >= n - remainder ? 1 : 0));
  });
  return out;
}

/** One paid-by leg as `walletSplitDeductionMinor` sees it — only the payer type and its minor
 *  amount matter. */
export interface WalletDeductionLeg {
  readonly type: string;
  readonly paidMinor: number;
}

/**
 * How much of the bill the group wallet already covers for the Split modals' DISPLAY total. When
 * the wallet co-pays alongside 2+ member payers, the members only distribute the REMAINDER
 * (a 150 bill with the wallet's 50 leaves 100 to split), so Σ displayed splits + wallet = bill.
 * This is a display/entry-space concept only — the WIRE `split_configs` always carry full-total
 * shares (the backend requires the split legs to sum to the expense amount and nets the wallet
 * itself; it rejects deducted sums with "Payment mismatch"). Any other payer shape (lone wallet,
 * wallet + one member, members only) keeps the whole total splittable.
 */
export function walletSplitDeductionMinor(
  payers: readonly WalletDeductionLeg[],
  totalMinor: number,
): number {
  const wallet = payers.find((p) => p.type === 'wallet');
  const memberPayerCount = payers.filter((p) => p.type !== 'wallet').length;
  if (!wallet || memberPayerCount < 2) return 0;
  return Math.min(wallet.paidMinor, totalMinor);
}

/** Sum of an array of minor-unit numbers. */
export function sumMinor(values: readonly number[]): number {
  return values.reduce((acc, v) => acc + v, 0);
}

/**
 * Convert per-entry bill-currency minor amounts to the group currency at `rate` (each rounded
 * half-up), then nudge the LAST eligible entry so the eligible parts sum EXACTLY to
 * `targetTotalMinor`. Mirrors the Flutter wire rule "last eligible absorbs rounding vs
 * selected_currency_amount" (§9) so `Σ group_paid_amount == selected_currency_amount` and
 * `Σ group_expense_amount == selected_currency_amount`.
 *
 * `eligible` (optional, same length as `partsMinor`) restricts which indices count toward the total
 * and which one absorbs the remainder — pass the split-enabled flags for `split_configs`; omit it
 * for `paid_by` (every payer counts).
 */
export function convertPartsMinor(
  partsMinor: readonly number[],
  rate: number,
  targetTotalMinor: number,
  eligible?: readonly boolean[],
): number[] {
  const out = partsMinor.map((p) => Math.round(p * rate));
  const idxs = out.map((_, i) => i).filter((i) => (eligible ? eligible[i] : true));
  if (idxs.length === 0) return out;
  const eligibleSum = idxs.reduce((acc, i) => acc + (out[i] ?? 0), 0);
  const last = idxs.at(-1) ?? 0;
  out[last] = (out[last] ?? 0) + (targetTotalMinor - eligibleSum);
  return out;
}
